import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ASSISTANT_API_BASE, ASSISTANT_API_PREFIX } from '../config/assistantApi';
import { logAssistantEvent } from '../utils/assistantTelemetry';

const API_CHAT_STREAM = `${ASSISTANT_API_BASE}${ASSISTANT_API_PREFIX}/chat/message/stream`;
const API_ESCALATE = `${ASSISTANT_API_BASE}${ASSISTANT_API_PREFIX}/escalate/tickets`;
const API_TICKET_BY_SESSION = (sid) =>
  `${ASSISTANT_API_BASE}${ASSISTANT_API_PREFIX}/escalate/tickets/session/${encodeURIComponent(sid)}`;

const POLL_INTERVAL_MS = 8000;
const POLL_MAX_ATTEMPTS = 60;
const CHAT_THREADS_STORAGE_KEY = 'assistantConversationsV1';
const CHAT_THREADS_TTL_MS = 1000 * 60 * 60 * 24 * 7; // 7 jours

function generateSessionId() {
  return 's_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);
}

function detectReferenceId(text) {
  if (!text) return null;
  var s = String(text);
  var m = s.match(/\bref[\s:=#-]*([A-Za-z0-9._-]{4,})\b/i);
  return m && m[1] ? m[1] : null;
}

function detectTransactionId(text) {
  if (!text) return null;
  var s = String(text);
  var m = s.match(/\b(trans(?:action)?[_-]?id|tx[_-]?id|trans_id)[\s:=#-]*([A-Za-z0-9._-]{4,})\b/i);
  if (m && m[2]) return m[2];
  var generic = s.match(/\b(TRX[-_A-Za-z0-9]{4,}|tx[-_A-Za-z0-9]{4,})\b/i);
  return generic && generic[1] ? generic[1] : null;
}

function getPageContext(pathname) {
  var p = String(pathname || '').toLowerCase();
  if (p.includes('/documentation')) return 'documentation';
  if (p.includes('/quickstart')) return 'quickstart';
  if (p.includes('/assistant')) return 'assistant';
  if (p.includes('/faq')) return 'faq';
  return 'other';
}

function safeTrimTitle(s) {
  var str = String(s || '').trim();
  if (!str) return '';
  return str.length > 48 ? str.slice(0, 48).trim() + '…' : str;
}

function generateThreadId() {
  return 'thread_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);
}

function deriveTitleFromMessages(messages) {
  if (!Array.isArray(messages)) return '';
  var firstUser = messages.find(function (m) { return m && m.role === 'user' && m.content; });
  if (!firstUser) return '';
  return safeTrimTitle(firstUser.content);
}

function loadThreadsFromStorage() {
  if (typeof window === 'undefined') return null;
  try {
    var raw = window.localStorage.getItem(CHAT_THREADS_STORAGE_KEY);
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    var savedAt = Number(parsed.savedAt || 0);
    if (!savedAt || Date.now() - savedAt > CHAT_THREADS_TTL_MS) return null;

    var conversations = Array.isArray(parsed.conversations) ? parsed.conversations : [];
    var safeConversations = conversations
      .map(function (c) {
        if (!c || typeof c !== 'object') return null;
        var id = typeof c.id === 'string' && c.id ? c.id : null;
        var sessionId = typeof c.sessionId === 'string' && c.sessionId ? c.sessionId : null;
        if (!id || !sessionId) return null;
        var messages = Array.isArray(c.messages) ? c.messages : [];
        var safeMessages = messages.map(function (m) {
          return m && typeof m === 'object' ? { ...m, streaming: false } : m;
        });
        return {
          id: id,
          sessionId: sessionId,
          title: typeof c.title === 'string' && c.title.trim() ? c.title : deriveTitleFromMessages(safeMessages) || 'Nouvelle discussion',
          messages: safeMessages,
          waitingForSupport: !!c.waitingForSupport,
          userEmail: typeof c.userEmail === 'string' ? c.userEmail : '',
          savedAt: Number(c.savedAt || savedAt || Date.now()),
        };
      })
      .filter(Boolean);

    var activeConversationId = typeof parsed.activeConversationId === 'string' ? parsed.activeConversationId : null;
    if (!activeConversationId || !safeConversations.some(function (c) { return c.id === activeConversationId; })) {
      activeConversationId = safeConversations.length ? safeConversations[0].id : null;
    }
    return { conversations: safeConversations, activeConversationId: activeConversationId };
  } catch (e) {
    return null;
  }
}

export function useAssistantThreads() {
  const persisted = useMemo(function () {
    return loadThreadsFromStorage();
  }, []);

  const [conversations, setConversations] = useState(persisted?.conversations || []);
  const [activeConversationId, setActiveConversationId] = useState(
    persisted?.activeConversationId || (persisted?.conversations?.[0]?.id ? persisted.conversations[0].id : null)
  );

  // fallback : créer un thread minimal
  useEffect(function () {
    if (conversations && conversations.length) return;
    var id = generateThreadId();
    var sessionId = generateSessionId();
    setConversations([
      {
        id: id,
        sessionId: sessionId,
        title: 'Nouvelle discussion',
        messages: [],
        waitingForSupport: false,
        userEmail: '',
        savedAt: Date.now(),
      },
    ]);
    setActiveConversationId(id);
  }, [conversations]);

  const activeConversation = useMemo(function () {
    return conversations.find(function (c) { return c.id === activeConversationId; }) || null;
  }, [conversations, activeConversationId]);

  const messages = activeConversation ? activeConversation.messages : [];
  const waitingForSupport = activeConversation ? !!activeConversation.waitingForSupport : false;
  const userEmail = activeConversation ? (activeConversation.userEmail || '') : '';
  const sessionId = activeConversation ? activeConversation.sessionId : null;

  const [loading, setLoading] = useState(false);
  const [pageUrl, setPageUrl] = useState('');
  const streamAbortRef = useRef(null);

  useEffect(function () {
    if (typeof window === 'undefined') return;
    setPageUrl(window.location.pathname + window.location.search || '');
  }, []);

  // persistence threads
  useEffect(function () {
    if (typeof window === 'undefined') return;
    try {
      var payload = {
        version: 1,
        activeConversationId: activeConversationId,
        conversations: (conversations || []).map(function (c) {
          return {
            id: c.id,
            sessionId: c.sessionId,
            title: c.title,
            messages: (c.messages || []).map(function (m) { return { ...m, streaming: false }; }),
            waitingForSupport: !!c.waitingForSupport,
            userEmail: typeof c.userEmail === 'string' ? c.userEmail : '',
            savedAt: Number(c.savedAt || Date.now()),
          };
        }),
        savedAt: Date.now(),
      };
      window.localStorage.setItem(CHAT_THREADS_STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {}
  }, [activeConversationId, conversations]);

  const updateActiveConversation = useCallback(function (updater) {
    setConversations(function (prev) {
      return (prev || []).map(function (c) {
        if (!activeConversationId || c.id !== activeConversationId) return c;
        return typeof updater === 'function' ? updater(c) : updater;
      });
    });
  }, [activeConversationId]);

  const setMessages = useCallback(function (nextMessages) {
    updateActiveConversation(function (c) {
      var msgs = typeof nextMessages === 'function' ? nextMessages(c.messages) : nextMessages;
      return { ...c, messages: Array.isArray(msgs) ? msgs : [], savedAt: Date.now() };
    });
  }, [updateActiveConversation]);

  const setUserEmail = useCallback(function (nextEmail) {
    updateActiveConversation(function (c) {
      return { ...c, userEmail: typeof nextEmail === 'string' ? nextEmail : c.userEmail, savedAt: Date.now() };
    });
  }, [updateActiveConversation]);

  const setWaitingForSupport = useCallback(function (nextVal) {
    updateActiveConversation(function (c) {
      return { ...c, waitingForSupport: !!nextVal, savedAt: Date.now() };
    });
  }, [updateActiveConversation]);

  // polling escalation (support)
  useEffect(function () {
    if (!waitingForSupport) return;
    var attempts = 0;
    var sid = sessionId;
    if (!sid) return;
    var interval = setInterval(function () {
      attempts += 1;
      if (attempts > POLL_MAX_ATTEMPTS) {
        clearInterval(interval);
        setWaitingForSupport(false);
        return;
      }
      fetch(API_TICKET_BY_SESSION(sid))
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (ticket) {
          if (ticket && ticket.status === 'answered' && ticket.response_text) {
            setMessages(function (prev) {
              return [
                ...prev,
                {
                  id: 'msg_support_' + Date.now(),
                  role: 'assistant',
                  content: '[Support] ' + ticket.response_text,
                  links: [],
                  snippets: [],
                },
              ];
            });
            setWaitingForSupport(false);
            clearInterval(interval);
          }
        })
        .catch(function () {});
    }, POLL_INTERVAL_MS);
    return function () { clearInterval(interval); };
  }, [waitingForSupport, sessionId, activeConversationId, setMessages, setWaitingForSupport]);

  function abortStream() {
    if (streamAbortRef.current) {
      try { streamAbortRef.current.abort(); } catch (e) {}
    }
    streamAbortRef.current = null;
  }

  async function runStream(textToSend, hist, onComplete, onPartial, imageDataUrl, imageDataUrls) {
    if (!sessionId) return;
    var assistantMsgId = 'msg_' + Date.now();
    var assistantSoFar = '';
    var startedAt = Date.now();
    var chunkCount = 0;

    function applyFinalUpdate(fc, links, snippets, suggestions, escalation, sources, confidenceLow) {
      assistantSoFar = String(fc || '');
      setMessages(function (prev) {
        return prev.map(function (m) {
          if (m.id !== assistantMsgId) return m;
          return {
            ...m,
            content: fc,
            links: links,
            snippets: snippets,
            suggestions: suggestions,
            sources: sources || [],
            confidenceLow: !!confidenceLow,
            escalation: escalation,
            streaming: false,
          };
        });
      });
    }

    function applyAppendUpdate(appendContent) {
      assistantSoFar += appendContent || '';
      setMessages(function (prev) {
        return prev.map(function (m) {
          if (m.id !== assistantMsgId) return m;
          return { ...m, content: m.content + appendContent };
        });
      });
    }

    setMessages(function (prev) {
      return [...prev, { id: assistantMsgId, role: 'assistant', content: '', links: [], snippets: [], streaming: true }];
    });
    setLoading(true);
    abortStream();

    var abortController = new AbortController();
    streamAbortRef.current = abortController;

    logAssistantEvent('stream_start', {
      sessionId: sessionId,
      pageUrl: pageUrl || window.location.pathname,
      historySize: (hist || []).length,
      hasImage: !!imageDataUrl || !!(imageDataUrls && imageDataUrls.length),
      imageCount: (imageDataUrls && imageDataUrls.length) || (imageDataUrl ? 1 : 0),
    });

    try {
      var res = await fetch(API_CHAT_STREAM, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: abortController.signal,
        body: JSON.stringify({
          content: textToSend,
          session_id: sessionId,
          page_url: pageUrl || window.location.pathname,
          history: hist || [],
          image_data_url: imageDataUrl || null,
          image_data_urls: Array.isArray(imageDataUrls) ? imageDataUrls : [],
        }),
      });
      if (!res.ok) throw new Error('API ' + res.status);

      var reader = res.body.getReader();
      var decoder = new TextDecoder();
      var buffer = '';
      while (true) {
        var chunk = await reader.read();
        if (chunk.done) break;
        chunkCount += 1;
        buffer += decoder.decode(chunk.value, { stream: true });
        var lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (var i = 0; i < lines.length; i++) {
          var line = lines[i];
          if (!line.startsWith('data: ')) continue;
          try {
            var data = JSON.parse(line.slice(6));
            var isFinal = !!(data.links || data.snippets || data.escalation !== undefined);
            if (isFinal) {
              var fc = data.content != null ? data.content : '';
              var links = data.links || [];
              var snippets = data.snippets || [];
              var suggestions = data.suggestions || [];
              var escalation = data.escalation !== undefined ? data.escalation : false;
              var sources = data.sources || [];
              var confidenceLow = data.confidence_low || false;

              applyFinalUpdate(fc, links, snippets, suggestions, escalation, sources, confidenceLow);
              if (onComplete && typeof onComplete === 'function') onComplete(fc, links, snippets);
              logAssistantEvent('stream_complete', {
                sessionId: sessionId,
                elapsedMs: Date.now() - startedAt,
                chunkCount: chunkCount,
                contentLength: String(fc || '').length,
                hasLinks: (links || []).length > 0,
                hasSnippets: (snippets || []).length > 0,
                escalation: !!escalation,
              });

              if (escalation) {
                setWaitingForSupport(true);
                var recentExchanges = (hist || []).slice(-6).map(function (h) {
                  return { role: h.role, content: h.content };
                });
                recentExchanges.push({ role: 'user', content: textToSend });
                recentExchanges.push({ role: 'assistant', content: String(fc || '').slice(0, 1000) });

                var refId = detectReferenceId(textToSend) || detectReferenceId(fc);
                var transId = detectTransactionId(textToSend) || detectTransactionId(fc);

                fetch(API_ESCALATE, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    question: textToSend,
                    page_url: pageUrl || window.location.pathname,
                    page_context: getPageContext(pageUrl || window.location.pathname),
                    session_id: sessionId,
                    chat_message_id: assistantMsgId,
                    email: userEmail || null,
                    ref: refId,
                    trans_id: transId,
                    recent_exchanges: recentExchanges,
                  }),
                }).catch(function () {});
              }
            } else if (data.content !== undefined) {
              var appendContent = data.content || '';
              applyAppendUpdate(appendContent);
              if (typeof onPartial === 'function') onPartial(assistantSoFar);
            }
          } catch (e) {}
        }
      }
    } catch (err) {
      if (err && (err.name === 'AbortError' || String(err.message || '').includes('Abort'))) {
        setLoading(false);
        streamAbortRef.current = null;
        setMessages(function (prev) {
          return prev.map(function (m) {
            if (m.id === assistantMsgId) return { ...m, streaming: false };
            return m;
          });
        });
        return;
      }
      logAssistantEvent('stream_error', {
        sessionId: sessionId,
        elapsedMs: Date.now() - startedAt,
        message: err && err.message ? err.message : 'unknown stream error',
      });
      setMessages(function (prev) {
        return prev.map(function (m) {
          if (m.id === assistantMsgId) {
            return { ...m, content: 'Désolé, le service est temporairement indisponible.', links: [], snippets: [], streaming: false };
          }
          return m;
        });
      });
    }

    logAssistantEvent('stream_end', {
      sessionId: sessionId,
      elapsedMs: Date.now() - startedAt,
      chunkCount: chunkCount,
    });
    setLoading(false);
    streamAbortRef.current = null;
  }

  function createConversation() {
    abortStream();
    setLoading(false);
    var id = generateThreadId();
    var newSessionId = generateSessionId();
    var thread = {
      id: id,
      sessionId: newSessionId,
      title: 'Nouvelle discussion',
      messages: [],
      waitingForSupport: false,
      userEmail: activeConversation ? activeConversation.userEmail : '',
      savedAt: Date.now(),
    };
    setActiveConversationId(id);
    setConversations(function (prev) {
      return [thread, ...(prev || [])];
    });
  }

  function switchConversation(threadId) {
    if (!threadId || threadId === activeConversationId) return;
    abortStream();
    setLoading(false);
    setWaitingForSupport(false);
    setActiveConversationId(threadId);
  }

  function renameConversation(threadId, nextTitle) {
    var title = safeTrimTitle(nextTitle);
    if (!threadId || !title) return;
    setConversations(function (prev) {
      return (prev || []).map(function (c) {
        if (c.id !== threadId) return c;
        return { ...c, title: title, savedAt: Date.now() };
      });
    });
  }

  function deleteConversation(threadId) {
    if (!threadId) return;
    abortStream();
    setLoading(false);
    setWaitingForSupport(false);

    setConversations(function (prev) {
      var next = (prev || []).filter(function (c) { return c.id !== threadId; });
      if (!next.length) {
        var id = generateThreadId();
        var sessionId2 = generateSessionId();
        next = [
          {
            id: id,
            sessionId: sessionId2,
            title: 'Nouvelle discussion',
            messages: [],
            waitingForSupport: false,
            userEmail: '',
            savedAt: Date.now(),
          },
        ];
      }
      if (threadId === activeConversationId) {
        setActiveConversationId(next[0] ? next[0].id : null);
      }
      return next;
    });
  }

  function updateTitleIfNeeded(nextMessages) {
    if (!activeConversationId) return;
    setConversations(function (prev) {
      return (prev || []).map(function (c) {
        if (c.id !== activeConversationId) return c;
        if (c.title && c.title !== 'Nouvelle discussion') return c;
        var title = deriveTitleFromMessages(nextMessages) || 'Nouvelle discussion';
        return { ...c, title: title, savedAt: Date.now() };
      });
    });
  }

  function setMessagesWithTitle(next) {
    var nextMessages = typeof next === 'function' ? next(messages) : next;
    updateTitleIfNeeded(nextMessages);
    setMessages(next);
  }

  const conversationsSorted = useMemo(function () {
    return (conversations || []).slice().sort(function (a, b) { return (b.savedAt || 0) - (a.savedAt || 0); });
  }, [conversations]);

  return {
    // API compatible avec AssistantChat
    messages,
    setMessages: setMessagesWithTitle,
    loading,
    waitingForSupport,
    userEmail,
    setUserEmail,
    sessionId,
    pageUrl,
    setWaitingForSupport,
    runStream,
    abortStream,
    // Sidebar / threads
    conversations: conversationsSorted,
    activeConversationId,
    createConversation,
    switchConversation,
    renameConversation,
    deleteConversation,
  };
}

